/**
 * Program: France
 * Language: French
 * Content: Index for all French translations
 */

import cookies from '../../common/fr/cookies'
import privacy from '../../common/fr/privacy'
import terms from './terms'
import { LangProps } from '../../types'

const fr: LangProps = {
  /** -------------------------------------------------------------------------- *
   * Document Titles
   * -------------------------------------------------------------------------- */
  document_title_suffix: 'Dyson échange avec Alchemy',
  document_title_home: 'Commencer',
  document_title_faq: 'Questions fréquentes',
  document_title_quiz: 'Questionnaire',
  document_title_privacy: 'Politique de confidentialité',
  document_title_terms: "Conditions générales (d'utilisation)",
  document_title_cookies: 'Politique en matière de cookies ',
  document_title_backoffice: 'Back-office',
  document_title_signOut: 'Se déconnecter',
  document_title_notFound: 'Page non trouvée',
  /** -------------------------------------------------------------------------- *
   * Links
   * -------------------------------------------------------------------------- */
  link_home: "Page d'accueil",
  link_tradein: 'Reprise',
  link_faq: 'FAQ',
  link_privacy: 'Politique de confidentialité',
  link_terms: "Conditions générales (d'utilisation)",
  link_cookies: 'Politique en matière de cookies ',
  link_cookie_preferences: 'Paramètres des cookies',
  powered_by: 'Alimenté par',
  /** -------------------------------------------------------------------------- *
   * Home
   * -------------------------------------------------------------------------- */
  home_hero_title: "Remplacez votre ancien aspirateur par la dernière technologie d'aspirateurs Dyson.",
  home_hero_pretitle: 'Profitez de notre offre de reprise',
  home_hero_intro:
    "Obtenez jusqu'à 300 € de remboursement après l'achat d'un aspirateur Dyson en nous renvoyant votre ancien aspirateur, toutes marques confondues (voir les conditions générales).",
  home_hero_cta: 'Renvoyer mon ancien aspirateur',
  home_hero2_title: 'Merci pour votre intérêt !',
  home_hero2_intro:
    'Malheureusement, notre offre de reprise est terminée. Veuillez retournez sur [www.Dyson.fr](https://www.dyson.fr) pour découvrir nos offres du moment.',
  home_hero2_outro:
    "Si votre demande d'échange a déjà été complétée, assurez-vous d'envoyer votre ancien aspirateur dans les deux semaines a partir de l'acceptation. Veuillez consulter notre e-mail précédent pour obtenir les instructions.",
  home_hero2_cta: "Retour à la page d'accueil [www.Dyson.fr](https://www.dyson.fr)",
  home_hero_sponsor: 'Alimenté par',
  home_steps_title: "Obtenez jusqu'à 300 € de remboursement en seulement 3 étapes",
  home_step_1_title: 'Inscription',
  home_step_1_desc:
    "Afin de recevoir un remboursement, vous devez avoir acheté un Dyson Gen5detect™ Absolute, Dyson V15 Detect Absolute™, Dyson V15s Detect Submarine™ ou un Dyson 360 Vis Nav™ entre le 6 mai 2024 et le 12 juin 2024 ¹.\n\nLa demande de reprise doit être soumise avant le 17 juillet 2024, via le portail de reprise.\n\nTous les aspirateurs doivent nous être envoyés dans les deux semaines suivant l'acceptation de votre demande. Le dernier jour pour renvoyer votre ancien aspirateur est le 31 juillet 2024.",
  home_step_2_title: 'Renvoyez-nous votre ancien aspirateur',
  home_step_2_desc:
    "Nous vous fournirons gratuitement un étiquette de retour prepayée pour nous renvoyer votre ancien aspirateur .\n\nEmballez simplement l'ancien aspirateur, collez l'étiquette et déposez le colis dans un dépôt UPS près de chez vous.",
  home_step_3_title: 'Recevez votre remboursement.',
  home_step_3_desc:
    'Dès réception de votre ancien aspirateur, nous traiterons votre remboursement.\n\nLe remboursement apparaîtra sur le compte que vous nous avez indiqué entre 30 et 45 jours après réception de votre ancien aspirateur.',
  home_steps_cta: 'Renvoyer mon ancien aspirateur',
  home_faq_title: 'Avez-vous des questions?',
  home_faq_button: 'Consultez notre FAQ',
  home_footer_terms:
    "¹ Sur Dyson.fr et dans les Dyson Demo Stores. Le montant du remboursement dépend de l'aspirateur acheté.",
  /** -------------------------------------------------------------------------- *
   * 404
   * -------------------------------------------------------------------------- */
  not_found_title: '404',
  not_found_desc: 'Nous sommes désolés, mais la page que vous cherchez est introuvable.',
  not_found_button: "Revenir sur la page d'accueil",
  /** -------------------------------------------------------------------------- *
   * FAQ
   * -------------------------------------------------------------------------- */
  faq_hero_title: 'FAQs',
  faq_search_placeholder: 'Appuyez pour chercher',
  faq_topics_title: 'Parcourir les thèmes',
  // -------------------------------------
  // Application Section
  // -------------------------------------
  faq_topic_1_title: 'Demande de reprise',
  faq_topic_1_q1: 'Où puis-je soumettre ma demande de reprise ?',
  faq_topic_1_a1: 'Veuillez visiter le [site Web](https://www.dyson.fr) pour déposer votre demande de reprise Dyson.',
  faq_topic_1_q2: "Quels produits Dyson sont éligibles pour participer à l'offre de reprise Dyson?",
  faq_topic_1_a2:
    'Lorsque vous achetez un nouveau Dyson Gen5detect™ Absolute, Dyson V15 Detect Absolute™, Dyson V15s Detect Submarine™ ou un Dyson 360 Vis Nav™ vous êtes éligible pour participer à le offre de reprise.',
  faq_topic_1_q3: "À quelle période l'offre de reprise a-t-elle lieu ?",
  faq_topic_1_a3:
    "Vous devez acheter un produit éligible auprès de Dyson ou d'un détaillant Dyson participant en France entre le mai 6 2024 et le 12 juin 2024.\n\nLa demande de reprise doit être soumise avant le 17 juillet 2024, via le portail de reprise.\n\nTous les aspirateurs doivent nous être envoyés dans les deux semaines suivant l'acceptation de votre demande. Le dernier jour pour renvoyer votre ancien aspirateur est le 31 juillet 2024.",
  faq_topic_1_q4: "Quels magasins participent à l'offre de reprise?",
  faq_topic_1_a4: '[www.Dyson.fr](https://www.dyson.fr) et les Dyson Demo Stores en France',
  faq_topic_1_q5: "Quelles conditions s'appliquent à l'offre de reprise?",
  faq_topic_1_a5:
    "Après avoir acheté un produit Dyson éligible et envoyé un ancien aspirateur pour reprise, vous recevrez un remboursement sur le compte bancaire que vous nous avez fourni.\n\nPour connaître l'intégralité des conditions générales, veuillez lire les Conditions générales (d'utilisation).",
  faq_topic_1_q6: 'Dois-je vivre en France pour participer à la promotion?',
  faq_topic_1_a6:
    "Non, mais vous devez avoir acheté le produit Dyson éligible sur dyson.fr (en ligne) ou dans un Dyson Demo Store situé en France. Vous devez également disposer d'un compte bancaire en France ou dans un autre pays de l'Espace Economique Européen pour participer à l'offre de reprise. Vous devez déposer votre colis en France.",
  faq_topic_1_q7: 'De quelles informations ai-je besoin pour remplir le formulaire?',
  faq_topic_1_a7:
    "Vous aurez besoin du nom du modèle et du numéro de série de votre nouveau produit Dyson, d'une preuve d'achat, des détails de l'aspirateur que vous souhaitez faire reprendre et de vos informations personnelles, y compris votre numéro de compte, pour recevoir le remboursement.",
  faq_topic_1_q8: "Et si j'ai perdu ma preuve d'achat pour mon nouvel aspirateur Dyson?",
  faq_topic_1_a8:
    "Si vous avez perdu votre preuve d'achat, veuillez demander une nouvelle preuve d'achat à votre détaillant afin que nous puissions vérifier la date et le lieu de l'achat. Sans preuve d'achat, nous ne pouvons pas traiter votre demande de reprise.",
  faq_topic_1_q9: "Qui gère l'offre de reprise Dyson?",
  faq_topic_1_a9: "L'offre de reprise est réalisée au nom de Dyson par Alchemy",
  faq_topic_1_q10: "Que se passe-t-il une fois que j'ai demandé l'échange?",
  faq_topic_1_a10:
    "Vous recevrez un e-mail avec la confirmation d'inscription et votre numéro de référence de reprise.",
  faq_topic_1_q11: 'Combien de temps faudra-t-il pour que ma demande de reprise soit approuvée?',
  faq_topic_1_a11:
    "Votre demande sera examinée et la décision concernant l'admission sera prise dans un délai d'un jour ouvrable (du lundi au vendredi). Une fois votre demande approuvée, vous recevrez un e-mail avec l'approbation et une étiquette d'expédition prépayée que vous pourrez imprimer et coller sur le colis de votre ancien aspirateur.",
  faq_topic_1_q12: "Je n'ai pas reçu d'e-mail ou ma candidature n'a pas été approuvée. Qui puis-je contacter?",
  faq_topic_1_a12:
    "Vous pouvez contacter l'équipe du service client d'Alchemy à [dyson@alchemyglobalsoutions.com](mailto:dyson@alchemyglobalsoutions.com).",
  // -------------------------------------
  // Shipping Section
  // -------------------------------------
  faq_topic_2_title: 'Emballage et expédition de votre ancien aspirateur',
  faq_topic_2_q1: 'Comment dois-je emballer mon ancien aspirateur?',
  faq_topic_2_a1:
    "Nous recommandons d'utiliser une boîte d'expédition renforcée suffisamment grande pour y placer votre aspirateur. Afin d'éviter les dommages lors du transport, ajoutez un carton sur l'unité principale et recouvrez le tube d'un matériau de protection si vous souhaitez l’envoyer. Après avoir sécurisé la boîte, imprimez l'étiquette prépayée et collez-la à un endroit bien visible sur l'emballage.",
  faq_topic_2_q2: "Puis-je utiliser n'importe quel type de carton?",
  faq_topic_2_a2: "Vous devez utiliser l'emballage le plus adapté pour que l'aspirateur arrive en bonne condition.",
  faq_topic_2_q3: "Que faut-il prendre en compte lors de l'expédition de l'ancien aspirateur?",
  faq_topic_2_a3: `
  L'aspirateur envoyé doit être intact et entier. L'aspirateur ne doit pas nécessairement être fonctionnel, mais ne doit pas être brisé en morceaux.
  Nous acceptons tout aspirateur, toutes marques confondues.
  Nous acceptons de reprendre:
  - Les aspirateur traîneaux
  - Les aspirateurs balais, avec ou sans-fil, sur batterie ou non
  - Les aspirateurs robots
  - Les aspirateurs laveurs

  Nous n'acceptons pas de reprendre:
  - Les aspirateurs à main

  Le collecteur ou le sac doit être vidé.
  Il n’est pas nécessaire d’inclure de tubes d'aspiration, de tuyaux, de câbles, de batteries ou d'autres accessoires. Seule l'unité principale de l'aspirateur doit être envoyée.
  Un seul aspirateur peut être envoyé par personne.
  `,
  faq_topic_2_q4: 'Expédier des batteries',
  faq_topic_2_a4:
    "Si l'appareil est équipé d'une batterie et que vous souhaitez l’envoyer avec l’aspirateur, celle-ci doit être intacte et exempte de dommages physiques. N'envoyez pas de batteries endommagées dans l'emballage.",
  faq_topic_2_q5: "Comment préparer le colis pour l'expédition?",
  faq_topic_2_a5:
    "Assurez-vous que la boîte est bien scellée et sécurisée.\n\nImprimez l'étiquette d'expédition prépayée que nous vous avons envoyée par e-mail et attachez-la solidement au colis.\n\nRemettez le colis à un point de dépôt UPS. \n\nLe poids du colis est fixé à l'avance à 5 kg. UPS enregistrera le poids exact de votre colis (au début ou plus tard si nécessaire) et nous facturera en conséquence. \n\nVeuillez ne pas remettre votre colis à un autre point de dépôt car il n'y sera pas accepté.",
  // -------------------------------------
  // After Shipping Section
  // -------------------------------------
  faq_topic_3_title: 'Après avoir fait reprendre mon aspirateur',
  faq_topic_3_q1: "Comment puis-je savoir si l'aspirateur que j'ai envoyé est arrivé?",
  faq_topic_3_a1:
    "Nous vous enverrons un e-mail une fois que nous aurons reçu l'aspirateur et confirmerons que le montant du remboursement sera transféré sur le compte que vous nous avez fournis dans votre demande d'échange.",
  faq_topic_3_q2: 'Combien de temps faut-il pour que la réception soit confirmée?',
  faq_topic_3_a2:
    "En règle générale, nous accuserons réception de l'aspirateur dans notre entrepôt par e-mail dans les 7 jours suivant l'expédition.",
  faq_topic_3_q3: "Que se passe-t-il si l'aspirateur soumis est rejeté?",
  faq_topic_3_a3:
    "Si l'aspirateur que vous nous avez envoyé ne correspond pas à ce que vous avez spécifié dans votre demande, notre service client vous contactera immédiatement.",
  faq_topic_3_q4: 'Combien de temps faudra-t-il pour recevoir mon paiement?',
  faq_topic_3_a4:
    "Après avoir vérifié l'achat d'un aspirateur Dyson éligible et reçu l'aspirateur d'échange, nous transférerons le remboursement sur le compte que vous nous avez fourni dans les 30 à 45 jours suivants la réception votre aspirateur.",
  // -------------------------------------
  // General Section
  // -------------------------------------
  faq_topic_4_title: 'En général',
  faq_topic_4_q1: "Qui dois-je contacter si j'ai des questions sur l'offre de reprise?",
  faq_topic_4_a1:
    "Si vous avez des questions concernant votre produit de reprise ou l'offre de reprise, vous pouvez contacter l'équipe Alchemy à [dyson@alchemyglobalsolutions.com](mailto:dyson@alchemyglobalsolutions.com)",
  faq_topic_4_q2: 'Quand puis-je contacter le support client?',
  faq_topic_4_a2:
    "Nous sommes disponibles en semaine (du lundi au vendredi) de 9h00 à 17h00 et faisons de notre mieux pour répondre à toutes les demandes dans un délai maximum d'un jour ouvrable.",
  /** -------------------------------------------------------------------------- *
   * Quiz
   * -------------------------------------------------------------------------- */
  // -------------------------------------
  // Customer Section
  // -------------------------------------
  quiz_s1_title: 'Vos coordonnées',
  quiz_s1_intro:
    'Nous vous contacterons par e-mail pour confirmer que nous avons reçu votre aspirateur de reprise et traité votre paiement.',
  quiz_s1_q1_label: 'Quel est votre nom complet?',
  quiz_s1_q1_placeholder: 'Nom et prénom',
  quiz_s1_q1_error_required: 'Veuillez saisir votre nom complet',
  quiz_s1_q1_error_invalid: 'Veuillez saisir votre prénom et votre nom',
  quiz_s1_q2_label: 'Quelle est votre adresse email?',
  quiz_s1_q2_placeholder: 'votrenom@exemple.fr',
  quiz_s1_q2_error_required: 'Veuillez saisir votre adresse email',
  quiz_s1_q2_error_invalid: 'Veuillez saisir une adresse email valide',
  quiz_s1_button: 'Étape suivante',
  quiz_s1_button_hint: 'Veuillez remplir tous les champs avant de continuer',
  // -------------------------------------
  // Purchase Section
  // -------------------------------------
  quiz_s2_title: 'Vérifier votre dernier achat',
  quiz_s2_short_title: "Vérifier le reçu d'achat",
  quiz_s2_intro:
    "Nous ne pouvons accepter votre ancien aspirateur que si vous avez déjà acheté l'un de nos produits d'aspirateur Dyson sélectionnés dans le cadre de notre offre de remboursement.",
  quiz_s2_next_step: "Continuer vers l'aspirateur à faire reprendre",
  quiz_s2_button: 'Étape suivante',
  quiz_s2_q1_label: 'Quel modèle avez-vous acheté?',
  // -------------------------------------
  quiz_s2_serial_help_button: 'Où puis-je le trouver?',
  quiz_s2_serial_q1_label: 'Veuillez télécharger une photo du numéro de série',
  quiz_s2_serial_q1_placeholder: "Télécharger l'image • Max. 10 MB",
  quiz_s2_serial_q2_label: 'Numéro de série',
  quiz_s2_serial_q2_placeholder: '000-00-00000000',
  quiz_s2_serial_q2_error_required: 'Veuillez saisir le numéro de série',
  quiz_s2_serial_q2_error_schema: 'Veuillez saisir un numéro de série valide',
  quiz_s2_serial_q2_error_maxlength: '15 caractères maximum autorisés',
  // -------------------------------------
  quiz_s2_receipt_q1_label: 'Veuillez télécharger une photo de la facture',
  quiz_s2_receipt_q1_placeholder: "Télécharger l'image • Max. 10 MB",
  quiz_s2_receipt_q2_label: "Date d'achat",
  quiz_s2_receipt_q2_placeholder: 'JJ/MM/AAAA',
  quiz_s2_receipt_q2_error_required: 'Veuillez saisir la date',
  quiz_s2_receipt_q2_error_invalid: 'La date doit être valide',
  quiz_s2_receipt_q2_error_length: 'La date doit être valide',
  quiz_s2_receipt_q2_error_future: 'La date ne peut pas être dans le futur',
  quiz_s2_receipt_q2_error_range: 'La date doit être comprise entre le 26 avril et le 12 juin 2024',
  quiz_s2_receipt_q3_label: 'Magasin',
  quiz_s2_receipt_q3_placeholder: 'Veuillez sélectionner',
  quiz_s2_receipt_q3_error_required: 'Veuillez sélectionner un magasin',
  // -------------------------------------
  quiz_s2_file_error_required: 'Veuillez télécharger un fichier',
  quiz_s2_file_error_size: 'Le fichier est trop volumineux',
  quiz_s2_file_error_format: 'Format de fichier non pris en charge',
  quiz_s2_file_error_invalid: 'Image invalide',
  quiz_s2_file_error_processing: "Erreur de traitement de l'image",
  // -------------------------------------
  serial_under_battery_title: 'Sous la batterie',
  serial_under_battery_desc: 'Le numéro de série est imprimé sous la batterie en bas de la poignée',
  serial_behind_bin_title: 'À l’arrière du collecteur transparent',
  serial_behind_bin_desc:
    'Veuillez regarder à l’arrière du collecteur transparent en face de la gâchette pour voir le numéro de série',
  serial_inside_handle_title: 'Derrière la batterie',
  serial_inside_handle_desc: 'Retirez la batterie pour révéler le numéro de série dans la poignée de la machine',
  serial_helper_exit_button: 'Retour',
  // -------------------------------------
  // Trade-in Section
  // -------------------------------------
  quiz_s3_title: 'Votre ancien aspirateur',
  quiz_s3_short_title: 'Ancien aspirateur',
  quiz_s3_intro: "Indiquez l'aspirateur vous souhaitez faire reprendre.",
  quiz_s3_next_step: "Continuer vers l'adresse de contact",
  quiz_s3_button: 'Étape suivante',
  // -------------------------------------:
  tradein_q1_label: 'Qui est le fabricant?',
  tradein_q1_option_1: 'Dyson',
  tradein_q1_option_2: 'Autre',
  tradein_q2_label: 'Confirmation des conditions de reprise Dyson',
  tradein_q2_answer: 'Je confirme que mon aspirateur à renvoyer correpond',
  tradein_q2_link: ' aux conditions.',
  tradein_q2_term_1:
    "La partie principale de mon aspirateur est intacte (il n'est pas nécessaire qu'elle soit fonctionnelle).",
  tradein_q2_term_2: 'Mon aspirateur est un modèle de taille normale avec un cordon ou une batterie.',
  tradein_q2_term_3: 'Le sac à poussière de mon aspirateur est complètement vide.',
  tradein_q2_term_4: 'Je fais reprendre un aspirateur éligible.',
  // -------------------------------------
  valuation_title: 'La valeur de votre aspirateur est',
  valuation_desc_1: 'Nous enverrons le remboursement dans les 30 à 45 jours suivants la réception votre aspirateur.',
  valuation_desc_2:
    "S'il y a des divergences entre votre description et l'aspirateur que nous avons reçu, des conditions peuvent s'appliquer.",
  valuation_button: 'Étape suivante',
  // -------------------------------------
  // Shipping Section
  // -------------------------------------
  shipping_title: 'Adresse de contact',
  shipping_short_title: 'Adresse de contact',
  shipping_intro: 'Nous avons besoin de votre adresse pour générer votre étiquette de retour prépayée.',
  shipping_next_step: 'Continuer vers les détails de remboursement.',
  shipping_button: 'Étape suivante',
  shipping_q1_label: 'Trouver une adresse',
  shipping_q1_placeholder: "Saisir l'adresse",
  shipping_address_label: 'Nom de la rue',
  shipping_address_placeholder: '',
  shipping_city_label: 'Ville',
  shipping_city_placeholder: '',
  shipping_postcode_label: 'Code Postal',
  shipping_postcode_placeholder: '',
  shipping_country_label: 'Pays',
  shipping_country_placeholder: 'Veuillez sélectionner',
  shipping_no_address_match: "Vous ne voyez pas votre adresse ? Veuillez l'ajouter manuellement.",
  // -------------------------------------
  // Payment Section
  // -------------------------------------
  payment_title: 'Détails de remboursement',
  payment_short_title: 'Détails de remboursement',
  payment_intro:
    "Veuillez fournir les coordonnées bancaires pour recevoir le remboursement. Veuillez vérifier attentivement ces informations car nous ne pouvons accepter aucune responsabilité en cas d'erreur.",
  payment_next_step: 'Dernière étape',
  // -------------------------------------
  payment_account_label: 'Titulaire du compte',
  payment_account_placeholder: 'Nom du titulaire du compte',
  payment_account_error_required: 'Veuillez saisir le nom du titulaire du compte',
  // -------------------------------------
  payment_iban_label: 'IBAN',
  payment_iban_placeholder: 'par exemple FR00 0000 0000 0000 0000 00',
  payment_iban_error_required: 'Veuillez saisir votre IBAN',
  // -------------------------------------:
  payment_submit_button: 'Étape suivante',
  payment_submit_hint: 'Veuillez remplir tous les champs avant de soumettre',
  // -------------------------------------
  payment_terms_text_1: "J'ai lu et j'accepte les",
  payment_terms_link_1: 'Conditions Générales',
  payment_terms_text_2: 'et le',
  payment_terms_link_2: 'règlement général sur la protection des données (RGPD)',
  payment_terms_text_3: ' ',
  // -------------------------------------
  confirm_title: 'Confirmer les détails du remboursement',
  confirm_desc: 'Veuillez vous assurer que les détails du compte que vous avez saisis sont corrects.',
  confirm_button: 'Confirmer',
  confirm_back_button: 'Retour',
  // -------------------------------------
  // Order Confirmation Section
  // -------------------------------------
  order_title_1: 'Merci ',
  order_title_2: 'Votre inscription a été effectuée avec succès.',
  order_confirmation_title: 'Quelles sont les prochaines étapes ?',
  order_confirmation_step1_title: 'Veuillez patienter pendant que nous vérifions vos coordonnées',
  order_confirmation_step1_content: 'Nous devons procéder à une vérification de vos informations.',
  order_confirmation_step2_title: 'Emballez votre ancien aspirateur et envoyez-le nous',
  order_confirmation_step2_content:
    "Nous vous enverrons gratuitement par e-mail une étiquette de retour ainsi que des instructions d'expédition.\n\nEmballez soigneusement votre ancien aspirateur dans une boîte appropriée et collez l'étiquette prépayée sur l'emballage de manière à ce qu'elle soit clairement visible.",
  order_confirmation_step3_title: 'Recevez votre remboursement',
  order_confirmation_step3_contentA:
    "Dès que nous recevrons votre ancien aspirateur, nous le vérifierons. S'il correspond à votre description, nous effectuerons le remboursement entre 30 jours et 45 jours suivants réception de votre aspirateur.",
  order_confirmation_step3_contentB:
    "Dès que nous recevrons votre ancien aspirateur, nous le vérifierons. S'il correspond à votre description, nous effectuerons le remboursement entre 30 jours et 45 jours suivants réception de votre aspirateur.",
  order_confirmation_step3_contentC: 'Titulaire du compte',
  order_confirmation_step3_contentD: 'Le paiement sera effectué entre 30 à 45 jours après réception de votre produit.',
  /** -------------------------------------------------------------------------- *
   * Errors
   * -------------------------------------------------------------------------- */
  server_error_Z6S:
    "Nous n'avons pas pu créer la commande car le numéro de série était déjà enregistré. Veuillez contacter le service client.",
  /** -------------------------------------------------------------------------- *
   * Cookie Consent
   * -------------------------------------------------------------------------- */
  cookie_statement:
    "En cliquant sur - Accepter tous les cookies - , vous acceptez le stockage de cookies sur votre appareil pour améliorer la navigation sur le site, analyser l'utilisation du site et nous aider dans nos efforts de marketing.",
  cookie_settings_button: 'Paramètres des cookies',
  cookie_accept_all_button: 'Accepter tous les cookies',
  cookie_allow_selected: 'Confirmer ma sélection',
  cookie_allow_all: 'Autoriser tout',
  cookie_essential_title: 'Cookies strictement nécessaires uniquement',
  cookie_essential_desc:
    "Ces cookies sont nécessaires au fonctionnement du site Web et ne peuvent pas être désactivés dans vos systèmes. En règle générale, ces cookies ne sont définis qu'en réponse aux actions que vous entreprenez et qui correspondent à une demande de service, telles que la définition de vos préférences de confidentialité, la connexion ou le remplissage de formulaires. Vous pouvez configurer votre navigateur pour bloquer ces cookies ou pour vous informer de ces cookies. Cependant, certaines zones du site Web ne fonctionneront pas. Ces cookies ne stockent aucune donnée personnelle.",
  cookie_performance_title: 'Cookies de performances',
  cookie_performance_desc:
    "Ces cookies nous permettent de compter les visites et les sources de trafic afin que nous puissions mesurer et améliorer les performances de notre site Web. Ils nous aident à répondre aux questions sur les pages les plus populaires, celles les moins utilisées et la manière dont les visiteurs se déplacent sur le site Web. Toutes les informations collectées par ces cookies sont agrégées et donc anonymes. Si vous n'autorisez pas ces cookies, nous ne pourrons pas savoir quand vous avez visité notre site Web.",
  cookie_functional_title: 'Cookies fonctionnels',
  cookie_functional_desc:
    "Ces cookies permettent au site Web de fournir des fonctionnalités et une personnalisation améliorées. Ils peuvent être paramétrés par nous ou par des tiers dont nous utilisons les services sur nos sites. Si vous n'autorisez pas ces cookies, tout ou partie de ces services pourraient ne pas fonctionner correctement.",
  cookie_targetting_title: 'Cookies à des fins de marketing',
  cookie_targetting_desc:
    "Ces cookies peuvent être définis via notre site Web par nos partenaires publicitaires. Ils peuvent être utilisés par ces sociétés pour établir un profil de vos intérêts et vous montrer des publicités pertinentes sur d'autres sites Web. Ils ne stockent pas directement de données personnelles, mais sont basés sur une identification unique de votre navigateur et de votre appareil Internet. Si vous n'autorisez pas ces cookies, vous recevrez des publicités moins ciblées.",
  /** -------------------------------------------------------------------------- *
   * Policy Pages
   * -------------------------------------------------------------------------- */
  terms_body: terms,
  privacy_body: privacy,
  cookies_body: cookies,
}

export default fr
